import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  Input,
  InputGroup,
  InputGroupRadio,
  InputGroupSelectRange,
  InputMultiSelect
  // InputGroupYearRange
} from '../common';
import InputSelect from '../common/InputSelect';

import AbilympicsProfessions from '../../data/AbilympicsProfession';
import {
  bestApprentice,
  genderOptions,
  ouvrierOption,
  fullRangeGroup
} from '../../data/professionOptions';
import Button from '../ui/button';
import { PlusIcon } from '@phosphor-icons/react';
import { TrashIcon } from '@heroicons/react/24/outline';

const GenericField = ({
  languages = [],
  fieldKey,
  charsLimit,
  requiredFieldMissing = false,
  formik,
  regions,
  professions,
  listSwissRegions,
  listProfessionalAssoc,
  listSwissFunction,
  listSwissJobTitle,
  listVocationalSchool,
  regionalEvents,
  participantLocations,
  establishmentsTypes,
  orgPrefix,
  profileTypeId,
  disabled
}) => {
  const { t } = useTranslation();

  const [updatedGenderOptions, setUpdatedGenderOptions] = useState(...genderOptions);
  const [updatedBestApprenticeOptions, setUpdatedBestApprenticeOptions] = useState([
    ...bestApprentice
  ]);
  const [updatedOuvrierOptions, setOuvrierOptions] = useState([...ouvrierOption]);
  const [fieldTranslation, setFieldTranslation] = useState({
    label: '',
    placeholder: '',
    error: ''
  });
  const [rankFieldTranslation, setRankFieldTranslation] = useState({ label: '', placeholder: '' });
  // const [yearFieldTranslation, setYearFieldTranslation] = useState({ label: '', placeholder: '' });

  useEffect(() => {
    const tGenders = t('form.genderOptions', { returnObjects: true });

    setUpdatedGenderOptions([
      {
        label: tGenders['masculin']?.[orgPrefix] ?? tGenders['masculin']?.default,
        value: 'masculin'
      },
      {
        label: tGenders['féminin']?.[orgPrefix] ?? tGenders['féminin']?.default,
        value: 'féminin'
      }
    ]);

    const tBestApprentice = t('form.bestApprenticeOptions', { returnObjects: true });

    setUpdatedBestApprenticeOptions([
      {
        label: tBestApprentice['oui']?.[orgPrefix] ?? tBestApprentice['oui']?.default,
        value: 'oui'
      },
      {
        label: tBestApprentice['no']?.[orgPrefix] ?? tBestApprentice['no']?.default,
        value: 'no'
      }
    ]);

    const tOuvrier = t('form.ouvrierOptions', { returnObjects: true });

    setOuvrierOptions([
      {
        label: tOuvrier['yes']?.[orgPrefix] ?? tOuvrier['yes']?.default,
        value: 'yes'
      },
      {
        label: tOuvrier['no']?.[orgPrefix] ?? tOuvrier['no']?.default,
        value: 'no'
      }
    ]);

    const tFieldTranslation = t(`form.fields.${fieldKey}`, { returnObjects: true });
    if (profileTypeId && tFieldTranslation?.[profileTypeId]) {
      setFieldTranslation(tFieldTranslation[profileTypeId]);
    } else {
      setFieldTranslation(
        tFieldTranslation?.[orgPrefix]
          ? tFieldTranslation[orgPrefix]
          : tFieldTranslation?.default
          ? tFieldTranslation.default
          : {
              label: `form.fields.${fieldKey}.label`,
              placeholder: `form.fields.${fieldKey}.placeholder`,
              error: `form.fields.${fieldKey}.error`
            }
      );
    }

    const tRankFieldTranslation = t(`form.fields.rankField`, { returnObjects: true });
    if (profileTypeId && tRankFieldTranslation?.[profileTypeId]) {
      setRankFieldTranslation(tRankFieldTranslation[profileTypeId]);
    } else {
      setRankFieldTranslation(
        tRankFieldTranslation?.[orgPrefix]
          ? tRankFieldTranslation[orgPrefix]
          : tRankFieldTranslation.default
      );
    }
  }, [fieldKey, orgPrefix, profileTypeId, t]);

  // Generic helper: get placeholder from individual field translation (e.g., recentProfession1)
  // Falls back to base field translation placeholder if specific one doesn't exist
  const getIndexedPlaceholder = (baseFieldKey, index) => {
    const specificKey = `${baseFieldKey}${index + 1}`; // e.g., "recentProfession1"
    const specificTranslation = t(`form.fields.${specificKey}`, { returnObjects: true });

    // Check if translation exists (i18next returns string key when translation is missing)
    const translationExists =
      typeof specificTranslation === 'object' && specificTranslation !== null;

    if (!translationExists) {
      // Translation doesn't exist, fall back to base field placeholder
      return fieldTranslation?.placeholder || '';
    }

    // Try to get placeholder from the specific field translation (matching org/profile logic)
    let specificPlaceholder;
    if (profileTypeId && specificTranslation?.[profileTypeId]?.placeholder) {
      specificPlaceholder = specificTranslation[profileTypeId].placeholder;
    } else if (specificTranslation?.[orgPrefix]?.placeholder) {
      specificPlaceholder = specificTranslation[orgPrefix].placeholder;
    } else if (specificTranslation?.default?.placeholder) {
      specificPlaceholder = specificTranslation.default.placeholder;
    }

    // Fall back to base field translation placeholder if specific one is empty
    return specificPlaceholder || fieldTranslation?.placeholder || '';
  };

  return (
    <>
      {fieldKey === 'firstName' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="firstName"
          labelClassName="font-bold mb-1"
          value={formik.values.firstName}
          touched={formik.touched.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.firstName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'familyName' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="familyName"
          labelClassName="font-bold mb-1"
          value={formik.values.familyName}
          touched={formik.touched.familyName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.familyName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'birthYear' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="birthYear"
          type="number"
          labelClassName="font-bold mb-1"
          value={formik.values.birthYear}
          touched={formik.touched.birthYear}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.birthYear}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'birthday' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="birthday"
          type="date"
          labelClassName={'font-semibold'}
          value={
            formik.values.birthday?.includes('T')
              ? formik.values.birthday.split('T')?.[0]
              : formik.values.birthday
          }
          touched={formik.touched.birthday}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.birthday}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'postalCode' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="postalCode"
          type="number"
          value={formik.values.postalCode}
          touched={formik.touched.postalCode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.postalCode}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'postcode' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="postcode"
          type="number"
          labelClassName="font-bold mb-1"
          value={formik.values.postcode}
          touched={formik.touched.postcode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.postcode}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'city' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="city"
          labelClassName="font-bold mb-1"
          value={formik.values.city}
          touched={formik.touched.city}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.city}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'abilympicsProfession' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="abilympicsProfession"
          labelClassName="font-bold mb-1"
          value={formik.values.abilympicsProfession}
          touched={formik.touched.abilympicsProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.abilympicsProfession}
          options={AbilympicsProfessions}
          onChange={(val) => {
            formik.setFieldValue('abilympicsProfession', val, true);
            setTimeout(() => formik.setFieldTouched('abilympicsProfession', true));
          }}
        />
      )}
      {fieldKey === 'gender' && (
        <InputGroupRadio
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="gender"
          labelClassName="font-bold mb-1"
          value={formik.values.gender}
          touched={formik.touched.gender}
          onBlur={formik.handleBlur}
          error={formik.errors.gender}
          setFieldValue={formik.setFieldValue}
          options={updatedGenderOptions}
        />
      )}
      {fieldKey === 'ouvrier' && (
        <InputGroupRadio
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName={'font-semibold'}
          className="my-1"
          name="ouvrier"
          value={formik.values.ouvrier}
          touched={formik.touched.ouvrier}
          onBlur={formik.handleBlur}
          error={formik.errors.ouvrier}
          setFieldValue={formik.setFieldValue}
          options={updatedOuvrierOptions}
        />
      )}
      {fieldKey === 'euroSkillsYear' && (
        <InputGroupSelectRange
          disabled={disabled}
          label={fieldTranslation?.label}
          year={{
            name: 'euroSkillsYear',
            type: 'number',
            value: formik.values.euroSkillsYear,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: fieldTranslation?.placeholder,
            error: formik.errors.euroSkillsYear,
            touched: formik.touched.euroSkillsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            name: 'euroSkillsRank',
            // onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.euroSkillsRank,
            touched: formik.touched.euroSkillsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup,
            value: formik.values.euroSkillsRank
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('euroSkillsYear', '', false);
            formik.setFieldValue('euroSkillsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'worldSkillsYear' && (
        <InputGroupSelectRange
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName="font-bold mb-1"
          year={{
            name: 'worldSkillsYear',
            type: 'number',
            value: formik.values.worldSkillsYear,
            placeholder: fieldTranslation?.placeholder,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.worldSkillsYear,
            touched: formik.touched.worldSkillsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            name: 'worldSkillsRank',
            touched: formik.touched.worldSkillsRank,
            value: formik.values.worldSkillsRank,
            onBlur: formik.handleBlur,
            error: formik.errors.worldSkillsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('worldSkillsYear', '', false);
            formik.setFieldValue('worldSkillsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'worldSkillsProfession' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="worldSkillsProfession"
          labelClassName={'font-semibold'}
          value={formik.values.worldSkillsProfession}
          touched={formik.touched.worldSkillsProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.worldSkillsProfession}
          options={professions}
          onChange={(val) => {
            formik.setFieldValue('worldSkillsProfession', val, true);
            setTimeout(() => formik.setFieldTouched('worldSkillsProfession', true));
          }}
        />
      )}
      {fieldKey === 'natFinalsRegion' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="natFinalsRegion"
          labelClassName="font-bold mb-1"
          value={formik.values.natFinalsRegion}
          touched={formik.touched.natFinalsRegion}
          onBlur={formik.handleBlur}
          error={formik.errors.natFinalsRegion}
          options={regions}
          onChange={(val) => {
            formik.setFieldValue('natFinalsRegion', val, true);
            setTimeout(() => formik.setFieldTouched('natFinalsRegion', true));
          }}
        />
      )}
      {fieldKey === 'region' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="region"
          value={formik.values.region}
          touched={formik.touched.region}
          onBlur={formik.handleBlur}
          error={formik.errors.region}
          options={listSwissRegions}
          onChange={(val) => {
            formik.setFieldValue('region', val, true);
            setTimeout(() => formik.setFieldTouched('region', true));
          }}
        />
      )}
      {fieldKey === 'mobileNumber' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="mobileNumber"
          labelClassName={'font-semibold'}
          value={formik.values.mobileNumber}
          touched={formik.touched.mobileNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.mobileNumber}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'montherLanguage' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="montherLanguage"
          value={formik.values.montherLanguage}
          touched={formik.touched.montherLanguage}
          onBlur={formik.handleBlur}
          error={formik.errors.montherLanguage}
          options={languages.map((l) => ({ value: l, label: l?.toUpperCase() }))}
          onChange={(val) => {
            formik.setFieldValue('montherLanguage', val, true);
            setTimeout(() => formik.setFieldTouched('montherLanguage', true));
          }}
        />
      )}
      {fieldKey === 'otherLanguage' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="otherLanguage"
          labelClassName={'font-semibold'}
          value={formik.values.otherLanguage}
          touched={formik.touched.otherLanguage}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.otherLanguage}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'specialJobTitle' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="specialJobTitle"
          value={formik.values.specialJobTitle}
          touched={formik.touched.specialJobTitle}
          onBlur={formik.handleBlur}
          error={formik.errors.specialJobTitle}
          options={listSwissJobTitle}
          onChange={(val) => {
            formik.setFieldValue('specialJobTitle', val, true);
            setTimeout(() => formik.setFieldTouched('specialJobTitle', true));
          }}
        />
      )}
      {fieldKey === 'profession' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="profession"
          value={formik.values.profession}
          touched={formik.touched.profession}
          onBlur={formik.handleBlur}
          error={formik.errors.profession}
          options={professions}
          profiessionPlaceholder
          onChange={(val) => {
            formik.setFieldValue('profession', val, true);
            setTimeout(() => formik.setFieldTouched('profession', true));
          }}
        />
      )}
      {fieldKey === 'swissChampionYear' && (
        <InputGroupSelectRange
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName={'font-semibold'}
          setFieldValue={formik.setFieldValue}
          year={{
            name: 'swissChampionYear',
            type: 'number',
            value: formik.values.swissChampionYear,
            placeholder: fieldTranslation?.placeholder,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.swissChampionYear,
            touched: formik.touched.swissChampionYear
          }}
          range={{
            name: 'swissChampionRank',
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            onChange: formik,
            onBlur: formik.handleBlur,
            error: formik.errors.swissChampionRank,
            value: formik.values.swissChampionRank,
            touched: formik.touched.swissChampionRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('swissChampionYear', '', false);
            formik.setFieldValue('swissChampionRank', '', false);
          }}
        />
      )}
      {fieldKey === 'currentCompanyCityDept' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="currentCompanyCityDept"
          labelClassName="font-bold mb-1"
          value={formik.values.currentCompanyCityDept}
          touched={formik.touched.currentCompanyCityDept}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.currentCompanyCityDept}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'traningCopmanyCityDept' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="traningCopmanyCityDept"
          labelClassName="font-bold mb-1"
          value={formik.values.traningCopmanyCityDept}
          touched={formik.touched.traningCopmanyCityDept}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.traningCopmanyCityDept}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'bestApprentice' && (
        <InputGroupRadio
          disabled={disabled}
          label={fieldTranslation?.label}
          className="my-2"
          name="bestApprentice"
          labelClassName="font-bold mb-1"
          value={formik.values.bestApprentice}
          touched={formik.touched.bestApprentice}
          // onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.bestApprentice}
          setFieldValue={formik.setFieldValue}
          options={updatedBestApprenticeOptions}
        />
      )}
      {fieldKey === 'bestWorker' && (
        <InputGroupRadio
          disabled={disabled}
          label={fieldTranslation?.label}
          className="my-22"
          name="bestWorker"
          labelClassName="font-bold mb-1"
          value={formik.values.bestWorker}
          touched={formik.touched.bestWorker}
          // onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.bestWorker}
          setFieldValue={formik.setFieldValue}
          options={bestApprentice}
        />
      )}
      {fieldKey === 'natFinalsYear' && (
        <InputGroupSelectRange
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName="font-bold mb-1"
          year={{
            name: 'natFinalsYear',
            type: 'number',
            value: formik.values.natFinalsYear,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: fieldTranslation?.placeholder,
            error: formik.errors.natFinalsYear,
            touched: formik.touched.natFinalsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            name: 'natFinalsRank',
            type: 'select',
            onBlur: formik.handleBlur,
            error: formik.errors.natFinalsRank,
            touched: formik.touched.natFinalsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup,
            value: formik.values.natFinalsRank
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('natFinalsYear', '', false);
            formik.setFieldValue('natFinalsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'trainingAndDiploma' && (
        <InputGroup
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName="font-bold mb-1"
          fields={[
            {
              name: 'trainingAndDiploma1',
              value: formik.values.trainingAndDiploma1,
              touched: formik.touched.trainingAndDiploma1,
              onChange: (e) => {
                formik.setFieldValue('trainingAndDiploma', e.target.value, true);
                formik.handleChange(e);
              },
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma1,
              placeholder: getIndexedPlaceholder('trainingAndDiploma', 0)
            },
            {
              name: 'trainingAndDiploma2',
              value: formik.values.trainingAndDiploma2,
              touched: formik.touched.trainingAndDiploma2,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma2,
              placeholder: getIndexedPlaceholder('trainingAndDiploma', 1)
            },
            {
              name: 'trainingAndDiploma3',
              value: formik.values.trainingAndDiploma3,
              touched: formik.touched.trainingAndDiploma3,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma3,
              placeholder: getIndexedPlaceholder('trainingAndDiploma', 2)
            }
          ]}
        />
      )}
      {fieldKey === 'currentJobName' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="currentJobName"
          labelClassName="font-bold mb-1"
          value={formik.values.currentJobName}
          touched={formik.touched.currentJobName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.currentJobName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'linkedinAddress' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="linkedinAddress"
          labelClassName={'font-semibold'}
          value={formik.values.linkedinAddress}
          touched={formik.touched.linkedinAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.linkedinAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'instagramAddress' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="instagramAddress"
          labelClassName="font-bold mb-1"
          value={formik.values.instagramAddress}
          touched={formik.touched.instagramAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.instagramAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'tiktokAddress' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="tiktokAddress"
          labelClassName="font-bold mb-1"
          value={formik.values.tiktokAddress}
          touched={formik.touched.tiktokAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.tiktokAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'facebookAddress' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="facebookAddress"
          labelClassName="font-bold mb-1"
          value={formik.values.facebookAddress}
          touched={formik.touched.facebookAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.facebookAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'associationProfession' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="associationProfession"
          value={formik.values.associationProfession}
          touched={formik.touched.associationProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.associationProfession}
          options={listProfessionalAssoc}
          onChange={(val) => {
            formik.setFieldValue('associationProfession', val, true);
            setTimeout(() => formik.setFieldTouched('associationProfession', true));
          }}
        />
      )}
      {fieldKey === 'vocationSchool' && (
        <InputSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="vocationSchool"
          value={formik.values.vocationSchool}
          touched={formik.touched.vocationSchool}
          onBlur={formik.handleBlur}
          error={formik.errors.vocationSchool}
          options={listVocationalSchool}
          onChange={(val) => {
            formik.setFieldValue('vocationSchool', val, true);
            setTimeout(() => formik.setFieldTouched('vocationSchool', true));
          }}
        />
      )}
      {fieldKey === 'functions' && (
        <InputMultiSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="functions"
          value={formik.values.functions}
          touched={formik.touched.functions}
          onBlur={formik.handleBlur}
          error={formik.errors.functions}
          options={listSwissFunction}
          onChange={(val) => {
            formik.setFieldValue('functions', val, true);
            setTimeout(() => formik.setFieldTouched('functions', true));
          }}
        />
      )}
      {fieldKey === 'regionalEvents' && (
        <InputMultiSelect
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'text-lg font-semibold'}
          className="my-1"
          name="regionalEvents"
          value={formik.values.regionalEvents}
          touched={formik.touched.regionalEvents}
          onBlur={formik.handleBlur}
          error={formik.errors.regionalEvents}
          options={regionalEvents}
          onChange={(val) => {
            formik.setFieldValue('regionalEvents', val, true);
            setTimeout(() => formik.setFieldTouched('regionalEvents', true));
          }}
        />
      )}
      {fieldKey === 'participantLocation' && (
        <InputSelect
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="participantLocation"
          value={formik.values.participantLocation}
          touched={formik.touched.participantLocation}
          onBlur={formik.handleBlur}
          error={formik.errors.participantLocation}
          options={participantLocations}
          onChange={(val) => {
            formik.setFieldValue('participantLocation', val, true);
            setTimeout(() => formik.setFieldTouched('participantLocation', true));
          }}
        />
      )}
      {fieldKey === 'freeText' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="freeText"
          labelClassName="font-bold mb-1"
          value={formik.values.freeText}
          touched={formik.touched.freeText}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.freeText}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'freeText2' && (
        <Input
          disabled={disabled}
          label={fieldTranslation?.label}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="freeText2"
          labelClassName="font-bold mb-1"
          value={formik.values.freeText2}
          touched={formik.touched.freeText2}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.freeText2}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'companyInformation' && (
        <>
          {formik.values?.companyInformation?.length > 0 && (
            <>
              {formik.values?.companyInformation?.map((_, index) => {
                const numberDisplay = index < 10 ? `0${index + 1}` : index + 1;

                return (
                  <div key={index} className="relative">
                    <InputGroup
                      disabled={disabled}
                      label={`${
                        numberDisplay && formik.values?.companyInformation?.length > 1
                          ? `[${numberDisplay}] `
                          : ''
                      }${fieldTranslation?.label}`}
                      labelClassName="font-bold mb-1"
                      fieldsClassName={'grid sm:grid-cols-3 sm:gap-x-2 grid-cols-1'}
                      fields={[
                        {
                          name: `companyInformation[${index}].name`,
                          value: formik.values.companyInformation?.[index]?.name || '',
                          touched: true || formik.touched.companyInformation?.[index]?.name, // marking touched as true by default to show errors if not interacted
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.name,
                          placeholder:
                            fieldTranslation?.name?.placeholder ||
                            `form.fields.${fieldKey}.name.placeholder`,
                          limit: charsLimit
                        },
                        {
                          name: `companyInformation[${index}].city`,
                          value: formik.values.companyInformation?.[index]?.city || '',
                          touched: true || formik.touched.companyInformation?.[index]?.city, // marking touched as true by default to show errors if not interacted
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.city,
                          placeholder:
                            fieldTranslation?.city?.placeholder ||
                            `form.fields.${fieldKey}.city.placeholder`,
                          limit: charsLimit
                        },
                        {
                          name: `companyInformation[${index}].department`,
                          value: formik.values.companyInformation?.[index]?.department || '',
                          touched: true || formik.touched.companyInformation?.[index]?.department, // marking touched as true by default to show errors if not interacted
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.department,
                          placeholder:
                            fieldTranslation?.department?.placeholder ||
                            `form.fields.${fieldKey}.department.placeholder`,
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `companyInformation[${index}].current`,
                          label:
                            fieldTranslation?.current?.label ||
                            `form.fields.${fieldKey}.current.label`,
                          checked: formik.values.companyInformation?.[index]?.current || false,
                          onChange: () => {
                            // Update the rest to false and this one to true only
                            formik.values?.companyInformation?.forEach((item, i) => {
                              if (i !== index) {
                                formik.setFieldValue(`companyInformation[${i}].current`, false);
                              }
                            });
                            formik.setFieldValue(`companyInformation[${index}].current`, true);
                          },
                          touched: true || formik.touched.companyInformation?.[index]?.current,
                          error: formik.errors.companyInformation?.[index]?.current,
                          inOneLine: true,
                          // className: 'checked:accent-black rounded-full mr-5 ',
                          limit: charsLimit
                        }
                      ]}
                    />

                    {formik.values?.companyInformation?.length > 1 && (
                      <button
                        type="button"
                        className="cursor-pointer text-red-500 absolute top-3 right-0"
                        onClick={() => {
                          const newCompanyInformation = formik.values.companyInformation.filter(
                            (_, i) => i !== index
                          );
                          formik.setFieldValue('companyInformation', newCompanyInformation);
                        }}>
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {formik.values?.companyInformation?.length < 2 && (
            <Button.ButtonRounded
              type="button"
              className={'!bg-grey-100 !text-black flex items-center gap-2'}
              onClick={() => {
                formik.setFieldValue('companyInformation', [
                  ...(formik.values.companyInformation || []),
                  {}
                ]);
              }}>
              <PlusIcon size={24} />{' '}
              {fieldTranslation?.addCompany?.label || `form.fields.${fieldKey}.addCompany.label`}
            </Button.ButtonRounded>
          )}
        </>
      )}
      {fieldKey === 'educationInformation' && (
        <>
          {formik.values?.educationInformation?.length > 0 && (
            <>
              {formik.values?.educationInformation?.map((_, index) => {
                const numberDisplay = index < 10 ? `0${index + 1}` : index + 1;

                return (
                  <div key={index} className="relative">
                    <InputGroup
                      disabled={disabled}
                      label={`${
                        numberDisplay && formik.values?.educationInformation?.length > 1
                          ? `[${numberDisplay}] `
                          : ''
                      }${fieldTranslation?.label}`}
                      labelClassName="font-bold mb-1"
                      fields={[
                        {
                          type: 'select',
                          name: `educationInformation[${index}].typeOfEstablishment`,
                          placeholder:
                            fieldTranslation?.typeOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.typeOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value: formik.values.educationInformation?.[index]?.typeOfEstablishment
                            ? {
                                value:
                                  formik.values.educationInformation?.[index]?.typeOfEstablishment,
                                label:
                                  formik.values.educationInformation?.[index]?.typeOfEstablishment
                              }
                            : null,
                          touched:
                            true ||
                            formik.touched.educationInformation?.[index]?.typeOfEstablishment,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.typeOfEstablishment,
                          options: establishmentsTypes?.map((type) => ({
                            value: type,
                            label: type
                          })),
                          onChange: (val) => {
                            formik.setFieldValue(
                              `educationInformation[${index}].typeOfEstablishment`,
                              val?.value || val,
                              true
                            );
                            setTimeout(() =>
                              formik.setFieldTouched(
                                `educationInformation[${index}].typeOfEstablishment`,
                                true
                              )
                            );
                          },
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].nameOfEstablishment`,
                          placeholder:
                            fieldTranslation?.nameOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.nameOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]?.nameOfEstablishment || '',
                          touched:
                            true ||
                            formik.touched.educationInformation?.[index]?.nameOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.nameOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].cityOfEstablishment`,
                          placeholder:
                            fieldTranslation?.cityOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.cityOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]?.cityOfEstablishment || '',
                          touched:
                            true ||
                            formik.touched.educationInformation?.[index]?.cityOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.cityOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].departmentOfEstablishment`,
                          placeholder:
                            fieldTranslation?.departmentOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.departmentOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]
                              ?.departmentOfEstablishment || '',
                          touched:
                            true ||
                            formik.touched.educationInformation?.[index]?.departmentOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error:
                            formik.errors.educationInformation?.[index]?.departmentOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].diplomaTitle`,
                          placeholder:
                            fieldTranslation?.diplomaTitle?.placeholder ||
                            `form.fields.${fieldKey}.diplomaTitle.placeholder`,
                          labelClassName: 'font-semibold',
                          value: formik.values.educationInformation?.[index]?.diplomaTitle || '',
                          touched:
                            true || formik.touched.educationInformation?.[index]?.diplomaTitle,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.diplomaTitle,
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `educationInformation[${index}].diplomaCompleted`,
                          label:
                            fieldTranslation?.diplomaCompleted?.label ||
                            `form.fields.${fieldKey}.diplomaCompleted.label`,
                          checked:
                            formik.values.educationInformation?.[index]?.diplomaCompleted || false,
                          onChange: () => {
                            const currentValue =
                              formik.values.educationInformation?.[index]?.diplomaCompleted ||
                              false;
                            formik.setFieldValue(
                              `educationInformation[${index}].diplomaCompleted`,
                              !currentValue
                            );
                          },
                          touched: formik.touched.educationInformation?.[index]?.diplomaCompleted,
                          error: formik.errors.educationInformation?.[index]?.diplomaCompleted,
                          inOneLine: true,
                          // className: 'checked:accent-black rounded-full mr-5 ',
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `educationInformation[${index}].current`,
                          label:
                            fieldTranslation?.current?.label ||
                            `form.fields.${fieldKey}.current.label`,
                          checked: formik.values.educationInformation?.[index]?.current || false,
                          onChange: () => {
                            // Update the rest to false and this one to true only
                            // formik.values?.educationInformation?.forEach((item, i) => {
                            //   if (i !== index) {
                            //     formik.setFieldValue(`educationInformation[${i}].current`, false);
                            //   }
                            // });
                            formik.setFieldValue(
                              `educationInformation[${index}].current`,
                              !formik.values.educationInformation?.[index]?.current || false
                            );
                          },
                          touched: formik.touched.educationInformation?.[index]?.current,
                          error: formik.errors.educationInformation?.[index]?.current,
                          inOneLine: true,
                          // className: 'checked:accent-black rounded-full mr-5 ',
                          limit: charsLimit
                        }
                        // {
                        //   name: 'educationInformation[0].yearOfCompletion',
                        //   placeholder: fieldTranslation?.yearOfCompletion?.placeholder || `form.fields.${fieldKey}.yearOfCompletion.placeholder`,
                        //   type: 'number',
                        //   labelClassName: 'font-semibold',
                        //   value: formik.values.educationInformation?.[0]?.yearOfCompletion || '',
                        //   touched: formik.touched.educationInformation?.[0]?.yearOfCompletion,
                        //   onChange: formik.handleChange,
                        //   onBlur: formik.handleBlur,
                        //   error: formik.errors.educationInformation?.[0]?.yearOfCompletion
                        // }
                      ]}
                    />

                    {formik.values?.educationInformation?.length > 1 && (
                      <button
                        type="button"
                        className="cursor-pointer text-red-500 absolute top-3 right-0"
                        onClick={() => {
                          const newEducationInformation = formik.values.educationInformation.filter(
                            (_, i) => i !== index
                          );
                          formik.setFieldValue('educationInformation', newEducationInformation);
                        }}>
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          <Button.ButtonRounded
            type="button"
            className={'!bg-grey-100 !text-black flex items-center gap-2'}
            onClick={() => {
              formik.setFieldValue('educationInformation', [
                ...formik.values.educationInformation,
                {}
              ]);
            }}>
            <PlusIcon size={24} />{' '}
            {fieldTranslation?.addEducation?.label || `form.fields.${fieldKey}.addEducation.label`}
          </Button.ButtonRounded>
        </>
      )}
      {fieldKey === 'recentProfession' && (
        <InputGroup
          disabled={disabled}
          label={fieldTranslation?.label}
          labelClassName={'font-semibold'}
          fields={[
            {
              name: 'recentProfession1',
              value: formik.values.recentProfession1,
              touched: formik.touched.recentProfession1,
              onChange: (e) => {
                formik.setFieldValue('recentProfession', e.target.value, true);
                formik.handleChange(e);
              },
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession1,
              placeholder: getIndexedPlaceholder('recentProfession', 0),
              limit: charsLimit
            },
            {
              name: 'recentProfession2',
              value: formik.values.recentProfession2,
              touched: formik.touched.recentProfession2,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession2,
              placeholder: getIndexedPlaceholder('recentProfession', 1),
              limit: charsLimit
            },
            {
              name: 'recentProfession3',
              value: formik.values.recentProfession3,
              touched: formik.touched.recentProfession3,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession3,
              placeholder: getIndexedPlaceholder('recentProfession', 2),
              limit: charsLimit
            }
          ]}
        />
      )}
      {fieldKey === 'participationType' && (
        <InputGroupRadio
          disabled={disabled}
          label={fieldTranslation?.label}
          helperTextTitle={fieldTranslation?.helperTextTitle}
          helperTextDescription={fieldTranslation?.helperTextDescription}
          labelClassName={'font-semibold'}
          className="my-1"
          name="participationType"
          value={formik.values.participationType}
          touched={formik.touched.participationType}
          onBlur={formik.handleBlur}
          error={formik.errors.participationType}
          setFieldValue={formik.setFieldValue}
          // Special case for translation
          options={t('form.participationType.values', { returnObjects: true })}
          optionDirection="col"
        />
      )}
      {fieldKey === 'consent' && (
        <Checkbox
          disabled={disabled}
          label={fieldTranslation?.label}
          name="consent"
          // className="checked:accent-black !w-& !h-6 rounded-full mr-5 "
          checked={formik.values.consent}
          onChange={() => formik.setFieldValue('consent', !formik.values.consent)}
          touched={formik.touched.consent}
          error={formik.errors.consent}
          inOneLine={true}
        />
      )}

      {requiredFieldMissing && (
        <p className="text-red-500 text-base font-normal -mt-2" data-testid="error-text">
          {fieldTranslation?.error || `form.fields.${fieldKey}.default.error`}
        </p>
      )}
    </>
  );
};

export default GenericField;
